import { type NextRequest, NextResponse } from "next/server";
import { handleApiError } from "./api-error-handler";
import { logger } from "./logger";
import { captureBffError, inferFeatureArea, shouldSkipBffCapture } from "./sentry";

/**
 * AsyncLocalStorage to store request ID for the duration of the request
 * This allows API client to access the request ID when making backend calls
 * Only available server-side (Node.js environment)
 */
let requestIdStorage: ReturnType<typeof createRequestIdStorage> | null = null;

function createRequestIdStorage() {
  // Only import AsyncLocalStorage in server-side context
  if (typeof window === "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { AsyncLocalStorage } = require("async_hooks") as typeof import("async_hooks");
    return new AsyncLocalStorage<string>();
  }
  return null;
}

function getRequestIdStorage() {
  if (!requestIdStorage && typeof window === "undefined") {
    requestIdStorage = createRequestIdStorage();
  }
  return requestIdStorage;
}

/**
 * Get the current request ID from AsyncLocalStorage
 * Used by API client to forward X-Request-Id header to backend
 * Returns undefined on client-side (where it's not needed)
 */
export function getCurrentRequestId(): string | undefined {
  const storage = getRequestIdStorage();
  return storage?.getStore() as string | undefined;
}

/**
 * Generate a UUID for request tracking
 */
function generateRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Set the request ID in AsyncLocalStorage for server actions
 * This allows the API client to forward X-Request-Id header to backend
 * Returns the request ID that was set
 */
export function setRequestIdForServerAction(requestId?: string): string {
  const id = requestId || generateRequestId();
  const storage = getRequestIdStorage();
  if (storage) {
    storage.enterWith(id);
  }
  return id;
}

/**
 * Extract or generate request ID from incoming request
 * Checks for X-Request-Id header first (from backend), then generates new one
 */
function getOrGenerateRequestId(request: NextRequest): string {
  const incomingRequestId = request.headers.get("x-request-id");
  return incomingRequestId || generateRequestId();
}

/**
 * Extract useful information from the request for logging.
 *
 * NOTE: query-string values are intentionally NOT logged. They can carry PII
 * (email, token, code, reset_token, etc.). We log only the param keys so
 * Loki searches by key still work without leaking values.
 */
function getRequestInfo(request: NextRequest, requestId: string) {
  const url = new URL(request.url);
  return {
    xRequestId: requestId, // Changed from requestId to xRequestId
    method: request.method,
    path: url.pathname,
    searchParamKeys: Array.from(url.searchParams.keys()),
    userAgent: request.headers.get("user-agent") || undefined,
    referer: request.headers.get("referer") || undefined,
  };
}

type ApiErrorShape = { message: string; status: number };

function isApiErrorShape(value: unknown): value is ApiErrorShape {
  return (
    typeof value === "object" &&
    value !== null &&
    "status" in value &&
    "message" in value &&
    typeof (value as { status: unknown }).status === "number"
  );
}

/**
 * Build a NextResponse for a thrown error and return its status, so the caller
 * can decide whether to capture to Sentry. Mirrors the previous in-place logic.
 */
function buildErrorResponse(
  error: unknown,
  requestId: string,
): { response: NextResponse; status: number } {
  // ApiError shape: surface as proper status code from the backend
  if (isApiErrorShape(error)) {
    const headers = new Headers();
    headers.set("X-Request-Id", requestId);
    return {
      response: NextResponse.json(
        { error: error.message || "Request failed" },
        { status: error.status, headers },
      ),
      status: error.status,
    };
  }
  handleApiError(error);

  const headers = new Headers();
  headers.set("X-Request-Id", requestId);
  return {
    response: NextResponse.json({ error: "Internal server error" }, { status: 500, headers }),
    status: 500,
  };
}

/**
 * Wrapper for route handlers that adds:
 * - Request ID generation and X-Request-Id header
 * - Request/response logging with elapsed time
 * - Error logging
 * - Sentry capture for BFF-side errors (deduped against ApiClient capture)
 */
export function withRequestLogging<T = unknown>(
  handler: (request: NextRequest, context?: T) => Promise<NextResponse>,
) {
  return async (request: NextRequest, context?: T): Promise<NextResponse> => {
    // Read X-Request-Id from incoming request (from backend) or generate new one
    const requestId = getOrGenerateRequestId(request);
    // Store in AsyncLocalStorage so API client can access it (server-side only)
    const storage = getRequestIdStorage();
    if (storage) {
      storage.enterWith(requestId);
    }

    const startTime = Date.now();
    const requestInfo = getRequestInfo(request, requestId);

    // Log request start
    logger.info(
      {
        ...requestInfo,
        event: "request_start",
      },
      `${request.method} ${requestInfo.path}`,
    );

    try {
      // Execute the handler (which can access requestId via getCurrentRequestId())
      const response = await handler(request, context);

      // Calculate elapsed time
      const elapsedTime = Date.now() - startTime;

      // Log successful response
      logger.info(
        {
          ...requestInfo,
          event: "request_complete",
          status: response.status,
          elapsedTime,
        },
        `${request.method} ${requestInfo.path} completed in ${elapsedTime}ms`,
      );

      // Add X-Request-Id header to response
      const headers = new Headers(response.headers);
      headers.set("X-Request-Id", requestId);

      return new NextResponse(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers,
      });
    } catch (error) {
      // Calculate elapsed time even for errors
      const elapsedTime = Date.now() - startTime;

      // Log error
      logger.error(
        {
          ...requestInfo,
          event: "request_error",
          elapsedTime,
          error:
            error instanceof Error
              ? {
                  message: error.message,
                  name: error.name,
                  stack: error.stack,
                }
              : String(error),
        },
        `${request.method} ${requestInfo.path} failed after ${elapsedTime}ms`,
      );

      const { response, status } = buildErrorResponse(error, requestId);

      // Capture to Sentry only for 5xx and only if not already captured by ApiClient.
      if (!shouldSkipBffCapture(error) && status >= 500) {
        captureBffError(error, {
          requestId,
          path: requestInfo.path,
          method: requestInfo.method,
          statusCode: status,
          featureArea: inferFeatureArea(requestInfo.path),
        });
      }

      return response;
    }
  };
}

/**
 * Helper for route handlers that need to access params
 * This version preserves the params context
 */
export function withRequestLoggingAndParams<P = unknown>(
  handler: (request: NextRequest, context: { params: Promise<P> }) => Promise<NextResponse>,
) {
  return async (request: NextRequest, context: { params: Promise<P> }): Promise<NextResponse> => {
    // Read X-Request-Id from incoming request (from backend) or generate new one
    const requestId = getOrGenerateRequestId(request);
    // Store in AsyncLocalStorage so API client can access it (server-side only)
    const storage = getRequestIdStorage();
    if (storage) {
      storage.enterWith(requestId);
    }

    const startTime = Date.now();
    const requestInfo = getRequestInfo(request, requestId);

    // Log request start
    logger.info(
      {
        ...requestInfo,
        event: "request_start",
      },
      `${request.method} ${requestInfo.path}`,
    );

    try {
      // Execute the handler (which can access requestId via getCurrentRequestId())
      const response = await handler(request, context);

      // Calculate elapsed time
      const elapsedTime = Date.now() - startTime;

      // Log successful response
      logger.info(
        {
          ...requestInfo,
          event: "request_complete",
          status: response.status,
          elapsedTime,
        },
        `${request.method} ${requestInfo.path} completed in ${elapsedTime}ms`,
      );

      // Add X-Request-Id header to response
      const headers = new Headers(response.headers);
      headers.set("X-Request-Id", requestId);

      return new NextResponse(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers,
      });
    } catch (error) {
      // Calculate elapsed time even for errors
      const elapsedTime = Date.now() - startTime;

      // Log error
      logger.error(
        {
          ...requestInfo,
          event: "request_error",
          elapsedTime,
          error:
            error instanceof Error
              ? {
                  message: error.message,
                  name: error.name,
                  stack: error.stack,
                }
              : String(error),
        },
        `${request.method} ${requestInfo.path} failed after ${elapsedTime}ms`,
      );

      const { response, status } = buildErrorResponse(error, requestId);

      if (!shouldSkipBffCapture(error) && status >= 500) {
        captureBffError(error, {
          requestId,
          path: requestInfo.path,
          method: requestInfo.method,
          statusCode: status,
          featureArea: inferFeatureArea(requestInfo.path),
        });
      }

      return response;
    }
  };
}
